import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, Crown, Sparkles, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";

type ProfileRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  plan: string;
  credits_used: number;
  created_at: string;
};

const planVariant: Record<string, string> = {
  free: "bg-muted text-muted-foreground",
  plus: "bg-primary/15 text-primary",
  pro: "bg-gradient-primary text-primary-foreground",
};

export default function AdminUsers() {
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [subEnvByUser, setSubEnvByUser] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<ProfileRow | null>(null);
  const [planValue, setPlanValue] = useState<string>("free");
  const [duration, setDuration] = useState<number>(30);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: profs }, { data: subs }] = await Promise.all([
      supabase
        .from("profiles")
        .select("id,email,full_name,plan,credits_used,created_at")
        .order("created_at", { ascending: false }),
      supabase
        .from("subscriptions")
        .select("user_id,environment,status,current_period_end")
        .in("status", ["active", "trialing", "past_due"]),
    ]);
    setProfiles((profs as ProfileRow[]) || []);
    const map: Record<string, string> = {};
    ((subs as any[]) || []).forEach((s) => {
      const stillValid = !s.current_period_end || new Date(s.current_period_end) > new Date();
      if (!stillValid) return;
      // Prefer manual over paddle if both exist (manual is admin-managed)
      if (map[s.user_id] === "manual") return;
      map[s.user_id] = s.environment;
    });
    setSubEnvByUser(map);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (p: ProfileRow) => {
    setEditing(p);
    setPlanValue(p.plan || "free");
    setDuration(30);
  };

  const savePlan = async () => {
    if (!editing) return;
    setSaving(true);
    const { error } = await supabase.rpc("admin_set_user_plan", {
      p_user_id: editing.id,
      p_plan: planValue,
      p_duration_days: duration,
    });
    setSaving(false);
    if (error) {
      toast.error("Could not update plan", { description: error.message });
      return;
    }
    toast.success("Plan updated");
    setEditing(null);
    load();
  };

  const filtered = profiles.filter(
    (p) =>
      !search ||
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: profiles.length,
    free: profiles.filter((p) => p.plan === "free").length,
    plus: profiles.filter((p) => p.plan === "plus").length,
    pro: profiles.filter((p) => p.plan === "pro").length,
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Users} label="Total users" value={stats.total} />
        <StatCard icon={Sparkles} label="Free" value={stats.free} />
        <StatCard icon={Crown} label="Plus" value={stats.plus} />
        <StatCard icon={Activity} label="Pro" value={stats.pro} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>Users</CardTitle>
          <Input
            placeholder="Search email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Credits used</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.email || "-"}</TableCell>
                      <TableCell>{p.full_name || "-"}</TableCell>
                      <TableCell>
                        <Badge className={planVariant[p.plan] || planVariant.free}>
                          {p.plan}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {p.credits_used}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(p.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {(() => {
                          const env = subEnvByUser[p.id];
                          const isCardPaid = env === "sandbox" || env === "live";
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isCardPaid}
                              title={isCardPaid ? "User pays by card — manage via payment provider" : ""}
                              onClick={() => openEdit(p)}
                            >
                              Change plan
                            </Button>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No users found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change plan</DialogTitle>
            <DialogDescription>
              {editing?.email} — manually set plan (used for bank-transfer customers).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={planValue} onValueChange={setPlanValue}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="plus">Plus</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {planValue !== "free" && (
              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  min={1}
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                />
                <p className="text-xs text-muted-foreground">
                  After this period, the user is auto-downgraded to Free unless renewed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={savePlan} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
          </div>
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
